import { useEffect, useRef, useState } from 'react';
import type { ActionLogEntry } from '../../../types/backend';

interface ActionLogSelection {
  /** The entry whose detail is open, kept in step with the live list. */
  selectedEntry: ActionLogEntry | null;
  select: (entry: ActionLogEntry | null) => void;
}

/**
 * The action log's open entry, including the one a deep link (?actionItem=oi-...) asks for. The linked entry
 * is fetched strongly by id, which lists it regardless of pagination, and selected by id once it is listed.
 */
export function useActionLogDeepLink(
  customerId: string,
  initialOpsItemId: string | null | undefined,
  entries: ActionLogEntry[],
  loadEntry: (opsitemId: string) => Promise<ActionLogEntry | null>,
): ActionLogSelection {
  const [selectedEntry, setSelectedEntry] = useState<ActionLogEntry | null>(null);

  // An entry belongs to its customer: a switch closes it while rendering, before the new list arrives.
  const [selectedFor, setSelectedFor] = useState(customerId);
  if (selectedFor !== customerId) {
    setSelectedFor(customerId);
    setSelectedEntry(null);
  }

  // Keep the open detail panel in sync with the live list: reflect a resolve and
  // close the panel when its item is deleted.
  useEffect(() => {
    setSelectedEntry((current) => {
      if (!current) return current;
      return entries.find((e) => e.opsitem_id === current.opsitem_id) ?? null;
    });
  }, [entries]);

  // Selecting from the list rather than from the fetch's continuation keeps the link working when the hook
  // hands out a new loadEntry while the fetch is in flight. Both are per customer, so a switch of customer
  // resolves the link afresh and never selects for one what the other asked. A link that goes away is
  // forgotten, so following the same one again opens it again.
  const resolvedDeepLink = useRef<string | null>(null);
  const [pendingDeepLink, setPendingDeepLink] = useState<{ customerId: string; opsItemId: string } | null>(null);
  useEffect(() => {
    if (!initialOpsItemId) {
      resolvedDeepLink.current = null;
      return;
    }
    const linkKey = `${customerId}/${initialOpsItemId}`;
    if (resolvedDeepLink.current === linkKey) return;
    resolvedDeepLink.current = linkKey;
    const link = { customerId, opsItemId: initialOpsItemId };
    setPendingDeepLink(link);
    loadEntry(initialOpsItemId).then((entry) => {
      if (!entry) setPendingDeepLink((pending) => (pending === link ? null : pending));
    });
  }, [customerId, initialOpsItemId, loadEntry]);

  // Adjusted while rendering rather than in an effect, so the row never shows without its detail.
  const linkedDeepEntry = pendingDeepLink?.customerId === customerId
    ? entries.find((e) => e.opsitem_id === pendingDeepLink.opsItemId)
    : undefined;
  if (linkedDeepEntry) {
    setSelectedEntry(linkedDeepEntry);
    setPendingDeepLink(null);
  }

  return { selectedEntry, select: setSelectedEntry };
}
